/**
 * KPIConfigModal — lets the Practice Manager choose and order up to 6 KPI cards.
 *
 * Left panel : KPI library grouped by domain with search (title + description).
 * Right panel: selected KPIs (max 6) in a 2×3 drag-to-reorder grid.
 *
 * Props:
 *   initialSelected  string[]  — current selection from parent's prefs
 *   onSave           async (selected: string[]) => void  — parent persists the change
 *   onClose          () => void
 *   saveError        string | null  — propagated from parent's useUserPreferences
 *
 * Phase 2 note: icons and markdown bold come from backend; modal is ready to
 * pass through whatever shape the API returns without layout changes.
 */
import React, { useState, useRef, useEffect } from 'react';
import { X, CheckCircle, Circle, GripVertical, Search, ChevronUp, ChevronDown } from 'lucide-react';

const MAX_SELECTED = 6;

const KPI_LIBRARY = [
  {
    domain: 'Staffing',
    color: 'var(--yellow)',
    kpis: [
      { id: 'staffing.coverage',      label: 'Staffing Coverage %',          desc: '% of shifts filled vs scheduled. Flags understaffing risk.' },
      { id: 'staffing.open_shifts',   label: 'Open Shift Count',             desc: 'Number of unfilled shifts requiring action today.' },
      { id: 'staffing.overtime',      label: 'Overtime Hours',               desc: 'Cumulative OT hours this period vs budget cap.' },
      { id: 'staffing.turnover',      label: 'Turnover Rate (30d)',          desc: 'Staff departures over the last 30 days as % of headcount.' },
      { id: 'staffing.fill_rate',     label: 'Fill Rate %',                  desc: '% of open positions filled vs target staffing level.' },
    ],
  },
  {
    domain: 'Scheduling & Access',
    color: 'var(--green)',
    kpis: [
      { id: 'access.same_day',        label: 'Same-Day Access Rate',         desc: '% of patients seen same day they requested an appointment.' },
      { id: 'access.wait_time',       label: 'Avg Wait Time (days)',         desc: 'Mean days between appointment request and first available slot.' },
      { id: 'access.open_slots',      label: 'Open Slots Today',             desc: 'Unfilled appointment slots available for today.' },
      { id: 'access.backlog',         label: 'Backlog Count',                desc: 'Patients waiting beyond the target access window.' },
      { id: 'access.utilization',     label: 'Appointment Utilization %',    desc: '% of scheduled slots that were kept vs no-show or cancel.' },
    ],
  },
  {
    domain: 'Population Health',
    color: 'var(--aqua)',
    kpis: [
      { id: 'pophealth.outreach',     label: 'Outreach Completion %',        desc: '% of targeted patients reached for preventive or follow-up outreach.' },
      { id: 'pophealth.high_risk',    label: 'High-Risk Patients Pending',   desc: 'High-risk patients without a completed care plan or follow-up.' },
      { id: 'pophealth.care_gaps',    label: 'Care Gap Closure Rate',        desc: '% of identified care gaps closed within the reporting period.' },
      { id: 'pophealth.care_plans',   label: 'Active Care Plans',            desc: 'Total patients with an active, in-progress care management plan.' },
      { id: 'pophealth.screenings',   label: 'Preventive Screenings Due',    desc: 'Patients overdue for a preventive screening per protocol.' },
    ],
  },
  {
    domain: 'Quality & Regulatory',
    color: 'var(--royal)',
    kpis: [
      { id: 'quality.performance',    label: 'Quality Performance Score',    desc: 'Composite score across quality measures vs practice target.' },
      { id: 'quality.a1c',            label: 'A1C Control Rate',             desc: '% of diabetic patients with A1C below the clinical threshold.' },
      { id: 'quality.hedis',          label: 'HEDIS Compliance %',           desc: 'Compliance rate across tracked HEDIS quality measures.' },
      { id: 'quality.open_compliance',label: 'Open Compliance Items',        desc: 'Unresolved audit, accreditation, or policy compliance items.' },
      { id: 'quality.task_completion',label: 'Task Completion Rate',         desc: '% of assigned operational tasks completed on time.' },
    ],
  },
  {
    domain: 'Financial Tracking',
    color: 'var(--yellow)',
    kpis: [
      { id: 'finance.revenue',        label: 'Revenue per Patient',          desc: 'Average revenue generated per patient visit this period.' },
      { id: 'finance.collections',    label: 'Collections Rate %',           desc: '% of billed charges successfully collected.' },
      { id: 'finance.denials',        label: 'Denied Claims %',              desc: '% of submitted claims denied by payer on first submission.' },
      { id: 'finance.days_ar',        label: 'Days in AR',                   desc: 'Average days between claim submission and payment receipt.' },
      { id: 'finance.variance',       label: 'Budget Variance %',            desc: 'Actual spend vs budgeted spend; negative = under budget.' },
    ],
  },
  {
    domain: 'Consumer Insights',
    color: 'var(--green)',
    kpis: [
      { id: 'consumer.nps',           label: 'Net Promoter Score',           desc: 'Likelihood of patients recommending the practice (−100 to 100).' },
      { id: 'consumer.satisfaction',  label: 'Patient Satisfaction %',       desc: 'Overall satisfaction rating from post-visit surveys.' },
      { id: 'consumer.complaints',    label: 'Complaints (30d)',             desc: 'Total patient complaints logged in the last 30 days.' },
      { id: 'consumer.resolved',      label: 'Resolved Complaints %',        desc: '% of logged complaints resolved within the SLA window.' },
      { id: 'consumer.return_visits', label: 'Return Visit Rate',            desc: '% of patients who returned for a follow-up within 90 days.' },
    ],
  },
  {
    domain: 'Practice Management',
    color: 'var(--aqua)',
    kpis: [
      { id: 'practice.active_patients',label: 'Active Patients',             desc: 'Total patients with at least one visit in the last 24 months.' },
      { id: 'practice.new_patients',   label: 'New Patients (MTD)',          desc: 'New patient registrations month-to-date.' },
      { id: 'practice.panel_util',     label: 'Provider Panel Utilization %',desc: '% of each provider\'s panel capacity currently in use.' },
      { id: 'practice.no_show',        label: 'No-Show Rate',                desc: '% of scheduled appointments where patient did not appear.' },
      { id: 'practice.referrals',      label: 'Referral Completion Rate',    desc: '% of outbound referrals with confirmed completion or follow-up.' },
    ],
  },
  {
    domain: 'Operational Reliability',
    color: 'var(--text-muted)',
    kpis: [
      { id: 'ops.intervention_time',  label: 'Avg Time to Intervention',     desc: 'Mean hours from signal detection to staff action or resolution.' },
      { id: 'ops.uptime',             label: 'System Uptime %',              desc: '% uptime for clinical and operational systems this period.' },
      { id: 'ops.response_time',      label: 'Signal Response Time (hrs)',   desc: 'Average hours to first response on an escalated signal.' },
      { id: 'ops.escalation_rate',    label: 'Escalation Rate',              desc: '% of signals that escalated to a higher severity level.' },
      { id: 'ops.reliability_score',  label: 'Reliability Score',            desc: 'Composite operational reliability score (1–10 scale).' },
    ],
  },
];

// Flat lookup: id → { label, desc, domain, color }
const KPI_META = Object.fromEntries(
  KPI_LIBRARY.flatMap(({ domain, color, kpis }) =>
    kpis.map((k) => [k.id, { label: k.label, desc: k.desc, domain, color }])
  )
);

export default function KPIConfigModal({ initialSelected, onSave, onClose, saveError }) {
  const [selected, setSelected] = useState(() => initialSelected ?? []);
  const [saving, setSaving] = useState(false);
  const [expandedDomains, setExpandedDomains] = useState({ Staffing: true });
  const [query, setQuery] = useState('');

  // Escape key closes modal
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Drag state for grid reorder
  const dragIndex = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const toggleKPI = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_SELECTED) return prev;
      return [...prev, id];
    });
  };

  const handleDragStart = (i) => { dragIndex.current = i; };
  const handleDragOver = (e, i) => { e.preventDefault(); setDragOver(i); };
  const handleDrop = (i) => {
    const from = dragIndex.current;
    if (from === null || from === i) { dragIndex.current = null; setDragOver(null); return; }
    setSelected((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
    dragIndex.current = null;
    setDragOver(null);
  };
  const handleDragEnd = () => { dragIndex.current = null; setDragOver(null); };

  const handleSave = async () => {
    setSaving(true);
    await onSave(selected);
    setSaving(false);
    onClose();
  };

  const toggleDomain = (domain) =>
    setExpandedDomains((p) => ({ ...p, [domain]: !p[domain] }));

  // Filter logic — searches label + desc; collapses when query active
  const q = query.trim().toLowerCase();
  const filteredLibrary = q
    ? KPI_LIBRARY.map((g) => ({
        ...g,
        kpis: g.kpis.filter(
          (k) => k.label.toLowerCase().includes(q) || k.desc.toLowerCase().includes(q)
        ),
      })).filter((g) => g.kpis.length > 0)
    : KPI_LIBRARY;

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(10,17,40,0.55)',
        zIndex: 1000, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: 'var(--white)', borderRadius: 14,
          boxShadow: 'var(--shadow-md)', width: '100%',
          maxWidth: 900, maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Modal header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--surface)' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy-deep)' }}>Configure Key Metrics</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-dark-secondary)', marginTop: 2 }}>
              Select up to {MAX_SELECTED} KPIs. Drag cards to reorder.
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dark-secondary)', padding: 4 }} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* Left — KPI library with search */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', borderRight: '1px solid var(--surface)' }}>
            {/* Search bar */}
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder="Search KPIs…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '7px 10px 7px 28px',
                  fontSize: 12.5, borderRadius: 7,
                  border: '1px solid var(--surface)',
                  background: 'var(--surface-warm)',
                  color: 'var(--navy-deep)',
                  outline: 'none',
                }}
              />
            </div>

            {filteredLibrary.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0', fontStyle: 'italic' }}>No KPIs match your search.</div>
            )}

            {filteredLibrary.map(({ domain, color, kpis }) => (
              <div key={domain} style={{ marginBottom: 6 }}>
                <button
                  type="button"
                  onClick={() => !q && toggleDomain(domain)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '7px 10px', borderRadius: 7,
                    background: (q || expandedDomains[domain]) ? 'var(--surface-warm)' : 'transparent',
                    border: 'none', cursor: q ? 'default' : 'pointer', textAlign: 'left',
                  }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--navy-deep)', flex: 1 }}>{domain}</span>
                  {!q && (
                    expandedDomains[domain]
                      ? <ChevronUp size={13} color="var(--text-dark-secondary)" />
                      : <ChevronDown size={13} color="var(--text-dark-secondary)" />
                  )}
                </button>

                {(q || expandedDomains[domain]) && (
                  <div style={{ paddingLeft: 24, marginTop: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {kpis.map((kpi) => {
                      const isSelected = selected.includes(kpi.id);
                      const isDisabled = !isSelected && selected.length >= MAX_SELECTED;
                      return (
                        <button
                          key={kpi.id}
                          type="button"
                          onClick={() => !isDisabled && toggleKPI(kpi.id)}
                          style={{
                            display: 'flex', alignItems: 'flex-start', gap: 8,
                            padding: '7px 10px', borderRadius: 6,
                            border: 'none', textAlign: 'left', width: '100%',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            background: isSelected ? 'var(--aqua-glow)' : 'transparent',
                            opacity: isDisabled ? 0.4 : 1,
                          }}
                        >
                          <span style={{ flexShrink: 0, marginTop: 1 }}>
                            {isSelected
                              ? <CheckCircle size={14} color="var(--aqua)" />
                              : <Circle size={14} color="var(--text-muted)" />}
                          </span>
                          <span style={{ flex: 1 }}>
                            <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--navy-deep)', lineHeight: 1.3 }}>{kpi.label}</span>
                            <span style={{ display: 'block', fontSize: 11, color: 'var(--text-dark-secondary)', marginTop: 1, lineHeight: 1.4 }}>{kpi.desc}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right — selected KPIs in 2×3 drag grid */}
          <div style={{ width: 340, padding: '14px 16px', overflowY: 'auto', flexShrink: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dark-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Selected ({selected.length}/{MAX_SELECTED})
            </div>

            {selected.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', padding: '8px 0' }}>
                Select KPIs from the library.
              </div>
            )}

            {/* Horizontal drag grid (matches page layout) */}
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(selected.length || 1, 3)}, 1fr)`, gap: 6 }}>
              {selected.map((id, i) => {
                const meta = KPI_META[id] || { label: id, desc: '', domain: '', color: 'var(--text-muted)' };
                return (
                  <div
                    key={id}
                    draggable
                    onDragStart={() => handleDragStart(i)}
                    onDragOver={(e) => handleDragOver(e, i)}
                    onDrop={() => handleDrop(i)}
                    onDragEnd={handleDragEnd}
                    style={{
                      padding: '8px 8px 8px',
                      borderRadius: 8,
                      background: dragOver === i ? 'var(--aqua-glow)' : 'var(--surface-warm)',
                      border: `1px solid ${dragOver === i ? 'var(--aqua)' : 'var(--surface)'}`,
                      cursor: 'grab',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                  >
                    {/* Top row: drag handle + remove */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                      <GripVertical size={13} color="var(--text-muted)" />
                      <button
                        type="button"
                        onClick={() => toggleKPI(id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, display: 'flex', alignItems: 'center' }}
                        aria-label={`Remove ${meta.label}`}
                      >
                        <X size={11} />
                      </button>
                    </div>
                    {/* Domain dot + label */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5 }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: meta.color, flexShrink: 0, marginTop: 3 }} />
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--navy-deep)', lineHeight: 1.3 }}>{meta.label}</span>
                    </div>
                  </div>
                );
              })}
              {/* Empty slots */}
              {Array.from({ length: MAX_SELECTED - selected.length }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  style={{
                    padding: '8px', borderRadius: 8, minHeight: 64,
                    border: '1.5px dashed var(--surface)',
                    background: 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>empty</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid var(--surface)' }}>
          {saveError
            ? <span style={{ fontSize: 12, color: 'var(--red-critical)' }}>{saveError}</span>
            : <span />
          }
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onClose} style={{ fontSize: 13, fontWeight: 600, padding: '7px 16px', borderRadius: 7, border: '1px solid var(--surface)', background: 'var(--white)', color: 'var(--text-dark-secondary)', cursor: 'pointer' }}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || selected.length === 0}
              style={{ fontSize: 13, fontWeight: 600, padding: '7px 16px', borderRadius: 7, border: 'none', background: 'var(--aqua)', color: 'white', cursor: saving || selected.length === 0 ? 'not-allowed' : 'pointer', opacity: saving || selected.length === 0 ? 0.6 : 1 }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
